import {
  ENTERS_FLOW_BY_STAGE,
  FINAL_PAGE_ROUTE,
  SUPPLEMENT_ARM,
  SUPPLEMENT_OFFER_ROUTE,
  SUPPLEMENT_PLAN,
  SUPPLEMENT_STAGE,
  SUPPLEMENTS_ROUTE,
  TIPS_ROUTE,
} from '../common/constants/supplement';
import type { SupplementArm, SupplementPlan, SupplementStage } from '../types/supplement';
import { stripFunnelPrefix } from '../../../../core/base/shared/funnelPath';

const ARMS = new Set<unknown>(Object.values(SUPPLEMENT_ARM));
const STAGES = new Set<unknown>(Object.values(SUPPLEMENT_STAGE));
const PLANS = new Set<unknown>(Object.values(SUPPLEMENT_PLAN));

function parsePath(
  path: string,
  funnelPrefix = '',
): { locale: string; page: string; subPage: string; segmentCount: number } {
  const segments = stripFunnelPrefix(path, funnelPrefix).split('/').filter(Boolean);
  const [locale = '', page = '', subPage = ''] = segments;

  return { locale, page, subPage, segmentCount: segments.length };
}

export function isSupplementFlowPage(path: string, funnelPrefix = ''): boolean {
  return parsePath(path, funnelPrefix).page === SUPPLEMENTS_ROUTE;
}

export function isSupplementOfferPage(path: string, funnelPrefix = ''): boolean {
  const { page, subPage, segmentCount } = parsePath(path, funnelPrefix);

  return segmentCount === 3 && page === SUPPLEMENTS_ROUTE && subPage === SUPPLEMENT_OFFER_ROUTE;
}

export function isFinalPage(path: string, funnelPrefix = ''): boolean {
  return parsePath(path, funnelPrefix).page === FINAL_PAGE_ROUTE;
}

export function isSupplementEntryPoint(toPath: string, fromPath: string, funnelPrefix = ''): boolean {
  if (toPath === fromPath) {
    return false;
  }

  const fromPage = parsePath(fromPath, funnelPrefix).page;
  const isFromFlowExit = fromPage === SUPPLEMENTS_ROUTE || fromPage === TIPS_ROUTE;

  return isFinalPage(toPath, funnelPrefix) && !isFromFlowExit;
}

export function supplementOfferPath(path: string, funnelPrefix = ''): string {
  const { locale } = parsePath(path, funnelPrefix);

  return `${funnelPrefix}/${locale}/${SUPPLEMENTS_ROUTE}/${SUPPLEMENT_OFFER_ROUTE}`;
}

export function finalPagePath(path: string, funnelPrefix = ''): string {
  const { locale } = parsePath(path, funnelPrefix);

  return `${funnelPrefix}/${locale}/${FINAL_PAGE_ROUTE}`;
}

export function toSupplementArm(featureValue: unknown): SupplementArm {
  return ARMS.has(featureValue) ? (featureValue as SupplementArm) : SUPPLEMENT_ARM.CONTROL;
}

export function toSupplementStage(storedValue: unknown): SupplementStage {
  return STAGES.has(storedValue) ? (storedValue as SupplementStage) : SUPPLEMENT_STAGE.NONE;
}

export function toSupplementPlan(storedValue: unknown): SupplementPlan {
  return PLANS.has(storedValue) ? (storedValue as SupplementPlan) : SUPPLEMENT_PLAN.THREE_MONTHS;
}

export function canEnterSupplementFlow(arm: SupplementArm, stage: SupplementStage): boolean {
  if (arm === SUPPLEMENT_ARM.CONTROL) {
    return false;
  }

  return ENTERS_FLOW_BY_STAGE[stage] ?? false;
}
